import { EnvironmentInjector, Injectable, Signal, WritableSignal, effect, inject, runInInjectionContext, signal, untracked } from '@angular/core';
import { NativeWorkbuddyAgentChatAdapter } from './native.workbuddy.agent.chat.adapter';

export interface BackgroundThreadState {
  streaming: boolean;
  unread: boolean;
}

interface PoolEntry {
  adapter: NativeWorkbuddyAgentChatAdapter;
  detachedAt: number;
  cleanup: () => void;
}

const MAX_BACKGROUND = 3;

@Injectable({ providedIn: 'root' })
export class BackgroundStreamManager {

  private readonly envInjector = inject(EnvironmentInjector);
  private readonly pool = new Map<string, PoolEntry>();

  private readonly _threadStates: WritableSignal<Map<string, BackgroundThreadState>> = signal(new Map());
  public readonly $threadStates: Signal<Map<string, BackgroundThreadState>> = this._threadStates.asReadonly();

  private readonly _lastCompleted: WritableSignal<{ threadId: string; at: number } | null> = signal(null);
  public readonly $lastCompleted: Signal<{ threadId: string; at: number } | null> = this._lastCompleted.asReadonly();

  public detach(threadId: string, adapter: NativeWorkbuddyAgentChatAdapter): void {

    // Evict oldest if at capacity
    if (this.pool.size >= MAX_BACKGROUND) {
      this.evictOldest();
    }

    // Start monitoring adapter status
    const cleanup = this.monitorAdapter(threadId, adapter);

    this.pool.set(threadId, { adapter, detachedAt: Date.now(), cleanup });

    // Set initial state based on current adapter status
    const status = adapter.status;
    const streaming = status === 'streaming' || status === 'submitted';
    this.updateThreadState(threadId, { streaming, unread: !streaming });
  }

  public reattach(threadId: string): NativeWorkbuddyAgentChatAdapter | null {

    const entry = this.pool.get(threadId);
    if (!entry) return null;

    entry.cleanup();
    this.pool.delete(threadId);
    this.removeThreadState(threadId);

    return entry.adapter;
  }

  public markRead(threadId: string): void {

    const current = this._threadStates().get(threadId);
    if (!current) return;

    if (!this.pool.has(threadId)) {
      this.removeThreadState(threadId);
    } else {
      this.updateThreadState(threadId, { ...current, unread: false });
    }
  }

  public cancel(threadId: string): void {

    const entry = this.pool.get(threadId);
    if (!entry) return;

    entry.cleanup();
    void entry.adapter.stop();
    this.pool.delete(threadId);
    this.removeThreadState(threadId);
  }

  public cancelAll(): void {

    const ids = [...this.pool.keys()];
    for (const id of ids) {
      this.cancel(id);
    }
  }

  private evictOldest(): void {

    let oldestId: string | null = null;
    let oldestTime = Infinity;

    for (const [id, entry] of this.pool) {
      if (entry.detachedAt < oldestTime) {
        oldestTime = entry.detachedAt;
        oldestId = id;
      }
    }

    if (oldestId) {
      this.cancel(oldestId);
    }
  }

  private monitorAdapter(threadId: string, adapter: NativeWorkbuddyAgentChatAdapter): () => void {

    let destroyed = false;
    let effectRef: ReturnType<typeof effect> | null = null;

    // Defer effect creation to escape the current reactive context (NG0602).
    queueMicrotask(() => {
      if (destroyed) return;

      effectRef = runInInjectionContext(this.envInjector, () => {
        return effect(() => {
          if (destroyed) return;

          const status = adapter.status;
          const streaming = status === 'streaming' || status === 'submitted';
          const current = untracked(() => this._threadStates().get(threadId));

          if (streaming) {
            this.updateThreadState(threadId, { streaming: true, unread: false });
          } else if (current?.streaming) {
            this.updateThreadState(threadId, { streaming: false, unread: true });
            if (!adapter.hasPendingApproval && !adapter.error) {
              this._lastCompleted.set({ threadId, at: Date.now() });
            }
          }
        });
      });
    });

    return () => {
      destroyed = true;
      effectRef?.destroy();
    };
  }

  private updateThreadState(threadId: string, state: BackgroundThreadState): void {

    this._threadStates.update(map => {
      const next = new Map(map);
      next.set(threadId, state);
      return next;
    });
  }

  private removeThreadState(threadId: string): void {

    this._threadStates.update(map => {
      const next = new Map(map);
      next.delete(threadId);
      return next;
    });
  }
}
